import { ActionIcon, Button, RingProgress, Text, TextInput } from "@mantine/core";
import { RotateCw } from "lucide-react";
import { useState } from "react";

import { AudioPlayer } from "../../ui/AudioPlayer";
import { nameFits } from "./limits";
import classes from "./voices.module.css";

export interface VoicePreviewProps {
  /** The voice to hear: a local file's object URL, or a designed voice's signed output. */
  url: string;
  /** The player's name: the file's, or live's "Design voice". */
  label: string;
  /** Live's words for what is wrong with the file; with any, it neither plays nor saves. */
  problems: string[];
  /** Back to the tab's pane: another take, another file, another design. */
  onReset: () => void;
  onSave: (name: string) => void;
  saving: boolean;
  /** 0 to 1 while the file goes up (live's ring in place of the reset). */
  progress: number | null;
  /** Why the save failed, in live's words. */
  failure: string | null;
}

/**
 * Live's preview of a recorded, uploaded or designed voice (`audio-file-preview`): the player
 * with its time and name, live's reset (or the upload's ring), the file's problems in red, then
 * "Name", empty, and "Save", which needs a name of 1 to 99 characters and no problem.
 */
export function VoicePreview(props: VoicePreviewProps) {
  const { url, label, problems, onReset, onSave, saving, progress, failure } = props;
  const [name, setName] = useState("");
  const blocked = problems.length > 0;
  return (
    <div className={classes.preview}>
      <div className={classes.previewHead}>
        <AudioPlayer url={url} name={label} seek={false} disabled={blocked} />
        {progress === null ? (
          <ActionIcon variant="subtle" aria-label="Start over" disabled={saving} onClick={onReset}>
            <RotateCw size={20} strokeWidth={1.5} />
          </ActionIcon>
        ) : (
          <RingProgress
            size={28}
            thickness={3}
            sections={[{ value: progress * 100, color: "indigo" }]}
            aria-label="Uploading"
          />
        )}
      </div>
      {problems.map((problem) => (
        <Text key={problem} className={classes.problem} role="alert">
          {problem}
        </Text>
      ))}
      <TextInput
        label="Name"
        value={name}
        disabled={saving || blocked}
        onChange={(event) => setName(event.currentTarget.value)}
      />
      <Button
        variant="light"
        loading={saving}
        disabled={blocked || !nameFits(name)}
        onClick={() => onSave(name.trim())}
      >
        Save
      </Button>
      {failure === null ? null : (
        <Text className={classes.problem} role="alert">
          {failure}
        </Text>
      )}
    </div>
  );
}
